import { notification } from 'ant-design-vue';
import { h } from 'vue';

// Position constant that can be imported by other components
export const POSITION = {
  TOP_LEFT: 'topLeft',
  TOP_RIGHT: 'topRight',
  BOTTOM_LEFT: 'bottomLeft',
  BOTTOM_RIGHT: 'bottomRight',
  TOP: 'top',
  BOTTOM: 'bottom',
};

// Configure default settings for notifications
notification.config({
  placement: POSITION.TOP_RIGHT,
  duration: 4.5,
  rtl: false,
});

// Toast adapter that mimics vue-toastification API
export const toast = {
  success(message, options = {}, title = '') {
    const key = options.key || `success_${Date.now()}_${Math.random()}`;
    notification.success({
      key,
      message: title,
      description: message,
      duration: options.timeout ? options.timeout / 1000 : 4.5,
      placement: options.placement || POSITION.TOP_RIGHT,
      style: 'background: #D1E7DD; color: #0f5132; border-radius: 5px;',
      ...options,
    });
    return key; // Return key so it can be dismissed later
  },

  error(message, options = {}, title = '') {
    const key = options.key || `error_${Date.now()}_${Math.random()}`;
    notification.error({
      key,
      message: title,
      description: message,
      duration: options.timeout ? options.timeout / 1000 : 4.5,
      placement: options.placement || POSITION.TOP_RIGHT,
      style: 'background: #F8D7DA; color: #842029; border-radius: 5px;',
      ...options,
    });
    return key;
  },

  warning(message, options = {}, title = '') {
    const key = options.key || `warning_${Date.now()}_${Math.random()}`;
    notification.warning({
      key,
      message: title,
      description: message,
      duration: options.timeout ? options.timeout / 1000 : 4.5,
      placement: options.placement || POSITION.TOP_RIGHT,
      style: 'background: #FFF3CD; color: #664d03; border-radius: 5px;',
      ...options,
    });
    return key;
  },

  info(message, options = {}, title = '') {
    const key = options.key || `info_${Date.now()}_${Math.random()}`;
    notification.info({
      key,
      message: title,
      description: message,
      duration: options.timeout ? options.timeout / 1000 : 4.5,
      placement: options.placement || POSITION.TOP_RIGHT,
      style: 'background: #CFF4FC; color: #055160; border-radius: 5px;',
      ...options,
    });
    return key;
  },

  // Alias methods for compatibility
  warn: function (message, options = {}, title = '') {
    return this.warning(message, options, title);
  },

  // Clear all notifications
  clear() {
    notification.destroy();
  },

  // Dismiss a specific notification by key
  dismiss(key) {
    if (key) {
      notification.close(key);
    }
  },

  // For backwards compatibility with some vue-toastification patterns
  default(message, options = {}) {
    return this.info(message, options);
  },

  // Advanced methods for better control
  loading(message, options = {}, title = '') {
    const key = options.key || `loading_${Date.now()}_${Math.random()}`;
    notification.info({
      key,
      message: title || 'Loading',
      description: message,
      duration: 0, // Don't auto-dismiss loading notifications
      placement: options.placement || POSITION.TOP_RIGHT,
      icon: () => h('a-spin', { size: 'small' }),
      ...options,
    });
    return key;
  },

  // Update an existing notification
  update(key, type, message, options = {}, title = '') {
    const config = {
      key,
      message: title || type.charAt(0).toUpperCase() + type.slice(1),
      description: message,
      duration: options.timeout ? options.timeout / 1000 : 4.5,
      placement: options.placement || POSITION.TOP_RIGHT,
      ...options,
    };

    switch (type) {
      case 'success':
        notification.success(config);
        break;
      case 'error':
        notification.error(config);
        break;
      case 'warning':
        notification.warning(config);
        break;
      case 'info':
      default:
        notification.info(config);
        break;
    }
    return key;
  },

  // Configure notification settings globally
  configure(config = {}) {
    const validConfig = {
      placement: config.placement || POSITION.TOP_RIGHT,
      duration: config.duration || 4.5,
      rtl: config.rtl || false,
      top: config.top,
      bottom: config.bottom,
      getContainer: config.getContainer,
      maxCount: config.maxCount,
    };

    notification.config(validConfig);
  },
};

// Create toast interface for store modules (replaces createToastInterface)
export const createToastInterface = () => toast;
export const useToast = () => toast;

// Default export for easier importing
export default toast;
